import { RefObject, useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { FormInstance } from 'antd/lib/form';

import { RootState } from '@share/utils';
import { insuranceActions, setCard } from '@store/slices';
import { ICardBooking } from '@common-types';
import { PaymentMethodBase } from '@components';
import { IUserCard } from '@share/common-types';

import type { IMultiplePayments } from '../types';

import './style.scss';
import { NULL_VALUE } from '@constants';

export interface IPaymentMethodProps {
  formRef: RefObject<FormInstance>;
  isRequired: boolean;
  display: boolean;
  onChangeCountryState: () => void;
  multiplePayments?: IMultiplePayments;
  setPaymentSelected?: (payment: string) => void;
  paymentSelected?: string;
  handlePaymentFormValidation?: any;
  scrollToPaymentSelect?: boolean;
  scrollToPaymentSelectDisabled?: () => void;
  stripeExpressCheckoutActive?: boolean;
  /** Hotels only (not vacation rentals): condo-style "Payment Methods" title when Stripe is enabled. */
  hotelsStripePaymentTitle?: boolean;
  /** Force-full insurance flow: lock the billing country to USA (non-editable). */
  disableCountrySelection?: boolean;
  /** Force-full insurance flow: lock the billing state to the page-1 selection (non-editable). */
  disableStateSelection?: boolean;
  /** Force-full insurance flow: hide the saved-cards picker (only a fresh card is valid there). */
  hideSavedCards?: boolean;
  /** Stripe flows: render the same form without any card input, keeping only the billing address. */
  addressOnly?: boolean;
}

export function PaymentMethod(props: IPaymentMethodProps) {
  const dispatch = useDispatch();
  const reviewBookStore = useSelector((state: RootState) => state.reviewBookStore);
  const menuStore = useSelector((state: RootState) => state.navigationMenuStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);

  const [refreshPayment, setRefreshPayment] = useState(false);

  const handleCardSelect = useCallback(
    (selectedCard: IUserCard) => {
      dispatch(setCard(selectedCard as ICardBooking));
      setRefreshPayment(true);
      dispatch(insuranceActions.setSelection(NULL_VALUE));
    },
    [dispatch],
  );

  const onDataRefreshed = useCallback(() => {
    setRefreshPayment(false);
  }, []);

  const {
    isRequired,
    formRef,
    display,
    onChangeCountryState,
    multiplePayments,
    setPaymentSelected,
    paymentSelected,
    handlePaymentFormValidation,
    scrollToPaymentSelect,
    scrollToPaymentSelectDisabled,
    stripeExpressCheckoutActive,
    hotelsStripePaymentTitle,
    disableCountrySelection,
    disableStateSelection,
    hideSavedCards,
    addressOnly,
  } = props;

  const { card, errorsField, loadingBooking } = reviewBookStore;
  const { account } = loginStore;
  const { items } = menuStore;

  return (
    <PaymentMethodBase
      form={NULL_VALUE}
      formRef={formRef}
      display={display}
      isRequired={isRequired}
      refreshData={refreshPayment}
      onDataRefreshed={onDataRefreshed}
      onChangeCountryState={onChangeCountryState}
      card={card}
      errorsField={errorsField}
      loading={loadingBooking}
      setCard={(c) => dispatch(setCard(c))}
      displaySave={account?.isPrivateTokenAccountType || account?.isPrivateLoginAccountType}
      disableInsurance={!account?.enableInsurance || items?.isRemoveInsurance}
      multiplePayments={multiplePayments}
      setPaymentSelected={setPaymentSelected}
      paymentSelected={paymentSelected}
      handlePaymentFormValidation={handlePaymentFormValidation}
      handleCardSelect={handleCardSelect}
      scrollToPaymentSelect={scrollToPaymentSelect}
      scrollToPaymentSelectDisabled={scrollToPaymentSelectDisabled}
      availableNewFlow={true}
      isCars={false}
      stripeExpressCheckoutActive={stripeExpressCheckoutActive}
      hotelsStripePaymentTitle={hotelsStripePaymentTitle}
      disableCountrySelection={disableCountrySelection}
      disableStateSelection={disableStateSelection}
      hideSavedCards={hideSavedCards}
      addressOnly={addressOnly}
    />
  );
}
